import { Helmet } from 'react-helmet';
import React, { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useGlobalState } from '../GlobalState';

function useApplePay({ onSuccess, onFailure, disabled = false }) {
  const [finalPricing] = useGlobalState('finalPricing');
  const [, setPaymentError] = useGlobalState('paymentError');
  const [, setPaymentSuccessRedirect] = useGlobalState(
    'paymentSuccessRedirect'
  );
  const [, setBookingTrialIssued] = useGlobalState('bookingTrialIssued');
  const [bookingId] = useGlobalState('bookingId');
  const buttonRef = useRef(null);

  useEffect(() => {
    if (disabled) {
      return;
    }
    if (
      document.querySelector(
        'script[src="https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js"]'
      )
    ) {
      return; // Script already loaded
    }
    const script = document.createElement('script');
    script.src = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [disabled]);

  const applePaySupported = Boolean(window?.ApplePaySession);

  const applePaySessionValidationToken = useRef(null);

  const requestApplePaySessionFromApi = useCallback(async () => {
    const params = {
      domain: window?.location?.hostname || 'swimbooker.com',
    };

    const axios_config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };

    const res = await axios
      .post(
        'payment/direct/get_apple_pay_session',
        {},
        { ...axios_config, params: params }
      )
      .catch((err) => {
        setPaymentError(err?.response?.data?.detail);
        onFailure();
      });

    if (res.status !== 200) {
      setPaymentError(res?.data?.detail);
      onFailure();
      return null;
    }

    applePaySessionValidationToken.current = res.data.session_validation_token;
    return res.data.session;
  }, [onFailure, setPaymentError]);

  const forwardApplePayPaymentToServer = useCallback(
    async (paymentDetails) => {
      const data = {
        booking_public_id: bookingId?.public_id,
        payload: paymentDetails,
        session_validation_token: applePaySessionValidationToken?.current,
        billing_address: {
          addressLine1: paymentDetails.billingContact.addressLines[0],
          postcode: paymentDetails.billingContact.postalCode,
          country: paymentDetails.billingContact.countryCode,
        },
      };

      const axios_config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      };

      try {
        const res = await axios.post(
          'payment/direct/register_with_apple_pay',
          data,
          axios_config
        );
        if (res.status !== 200) {
          setPaymentError(res?.data?.detail);
        } else {
          setPaymentSuccessRedirect(res?.data?.redirect);
          if (res?.data?.trial_issued !== undefined) {
            setBookingTrialIssued(Boolean(res?.data?.trial_issued));
          }
        }
        return res.status === 200;
      } catch (err) {
        setPaymentError(err?.response?.data?.detail);
        return false;
      }
    },
    [
      bookingId?.public_id,
      setBookingTrialIssued,
      setPaymentError,
      setPaymentSuccessRedirect,
    ]
  );

  const handleApplePayClick = useCallback(
    (e) => {
      const ApplePaySession = window?.ApplePaySession;
      if (!ApplePaySession) {
        return;
      }
      const request = {
        countryCode: 'GB',
        currencyCode: 'GBP',
        merchantCapabilities: ['supports3DS'],
        supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
        total: {
          label: 'Total Booking Cost',
          type: 'final',
          amount: finalPricing,
          paymentTiming: 'immediate',
        },
        requiredBillingContactFields: ['postalAddress'],
      };
      const applePayVersion = 3;
      const session = new ApplePaySession(applePayVersion, request);

      /**
       * Called to start the Apple Pay process
       */
      session.onvalidatemerchant = async (event) => {
        const merchantSession = await requestApplePaySessionFromApi();
        session.completeMerchantValidation(merchantSession);
      };

      session.onpaymentmethodchange = async (event) => {};

      /**
       * Called once the user authenticates the transaction with their password/Face ID/Touch ID
       */
      session.onpaymentauthorized = async (event) => {
        const approved = await forwardApplePayPaymentToServer(event.payment);
        if (approved) {
          session.completePayment({
            status: ApplePaySession.STATUS_SUCCESS,
          });
          onSuccess();
        } else {
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
          });
          onFailure();
        }
      };

      /**
       * Called when the payment UI is dismissed
       */
      session.oncancel = (event) => {};

      session.begin();
    },
    [
      finalPricing,
      forwardApplePayPaymentToServer,
      onFailure,
      onSuccess,
      requestApplePaySessionFromApi,
    ]
  );

  useEffect(() => {
    if (!buttonRef?.current) {
      return;
    }
    buttonRef.current.addEventListener('click', handleApplePayClick);
    return () => window.removeEventListener('click', handleApplePayClick);
  }, [handleApplePayClick]);

  const applePayButton = (
    <>
      <Helmet>
        <style>
          {`apple-pay-button {
            --apple-pay-button-width: 100%;
            --apple-pay-button-height: 3rem;
            --apple-pay-button-border-radius: 6px;
            --apple-pay-button-padding: 0px 0px;
            --apple-pay-button-box-sizing: border-box;
            display: block;
            --webkit-appearance: -apple-pay-button;
          }`}
        </style>
      </Helmet>
      <apple-pay-button
        ref={buttonRef}
        buttonstyle='black'
        type='plain'
        locale='en'
      ></apple-pay-button>
    </>
  );

  return [
    applePaySupported && !disabled,
    (applePaySupported && !disabled) ? applePayButton : null,
    handleApplePayClick,
  ];
}

export default useApplePay;
